import type { EnabledMfrrProductionUnit, MfrrBidResponse, MfrrMarketResult } from 'api/mfrr/mfrr.types'
import type { EamCopyFailureReason, EamCopyRow } from '../MfrrBidTable/MfrrBidTable.types'

export function mapMfrrBidsForEamCopy(
  bidsForCopy: MfrrBidResponse[],
  cmUnits: EnabledMfrrProductionUnit[],
  eamUnits: EnabledMfrrProductionUnit[]
): MfrrBidResponse[] {
  const eamUnitIdByName = new Map(eamUnits.map((unit) => [unit.name?.trim().toLowerCase() ?? '', unit.id]))
  const cmUnitNameById = new Map(cmUnits.map((unit) => [unit.id, unit.name?.trim().toLowerCase() ?? '']))

  return bidsForCopy.map((bid) => {
    if (bid.production_unit_id === null) {
      return bid
    }

    const mappedByName = eamUnitIdByName.get(cmUnitNameById.get(bid.production_unit_id) ?? '')
    const targetUnitId = mappedByName ?? bid.production_unit_id

    return {
      ...bid,
      production_unit_id: targetUnitId,
    }
  })
}

export function mapMfrrMarketResultsForEamCopy(
  marketResultsForCopy: MfrrMarketResult[],
  cmUnits: EnabledMfrrProductionUnit[],
  eamUnits: EnabledMfrrProductionUnit[]
): EamCopyRow[] {
  const eamUnitIdByName = new Map(eamUnits.map((unit) => [unit.name?.trim().toLowerCase() ?? '', unit.id]))
  const cmUnitIdByName = new Map(cmUnits.map((unit) => [unit.name?.trim().toLowerCase() ?? '', unit.id]))

  return marketResultsForCopy.map((marketResult) => {
    const normalizedProductionUnitName = marketResult.production_unit?.trim().toLowerCase() ?? ''
    const sourceUnitId = cmUnitIdByName.get(normalizedProductionUnitName)
    const targetUnitId = eamUnitIdByName.get(normalizedProductionUnitName) ?? sourceUnitId ?? null

    return {
      market: marketResult.market,
      direction: marketResult.direction,
      production_unit_id: targetUnitId,
      start_time: marketResult.start_time,
      volume: marketResult.volume,
    }
  })
}

export function getEamCopyFromFailureMessageKey(reason: EamCopyFailureReason): string {
  switch (reason) {
    case 'copy-source-loading':
      return 'Could not copy CM volume because CM bids are still loading.'
    case 'copy-no-cm-bids':
      return 'Could not copy CM volume because no locked CM bids exist for the selected day.'
    case 'copy-no-cm-results':
      return 'Could not copy CM volume because no CM results exist for the selected day.'
    case 'copy-no-matching-unit-configured':
      return 'Could not copy CM volume because no matching EAM unit is configured for the available CM bids.'
    case 'copy-target-rows-read-only':
      return 'Could not copy CM volume because target EAM rows are locked or read-only.'
  }
}
